import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, Menu, TreeDotsIcon, List, ListItem, EditIcon, ConfirmDialog } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { NoteVersionFormValidator } from "~/validators";
import { RenameDialog } from "../Dialogs/RenameDialog";

export const VersionOptions = ({ className, modificationId, name, onRename, onRevert }) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [showRevert, setShowRevert] = useState(false);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleShowRename = useCallback(() => setShowRename(true), []);
  const handleCloseRename = useCallback(() => setShowRename(false), []);
  const handleShowRevert = useCallback(() => setShowRevert(true), []);
  const handleCloseRevert = useCallback(() => setShowRevert(false), []);

  const handleRevert = useCallback(() => {
    handleCloseRevert();
    handleClose();
    typeof onRevert === "function" && onRevert(modificationId);
  }, [handleClose, handleCloseRevert, modificationId, onRevert]);

  const revertContent = t(
    "The current note version will not be lost by reverting the note to the chosen version. You will always be able to access all the changes made to the document."
  );

  const handleRename = useCallback(
    (data) => {
      handleCloseRename();
      handleClose();
      typeof onRename === "function" && onRename({ modification_id: modificationId, ...data });
    },
    [handleCloseRename, handleClose, modificationId, onRename]
  );

  return (
    <>
      <Menu
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        target={
          <IconButton
            variant="text gray"
            className={classNames("note-version-options", className, { open })}
            active={open}
            size="small"
          >
            <TreeDotsIcon />
          </IconButton>
        }
      >
        <List className="tw-py-2">
          {modificationId > 0 ? (
            <ListItem onClick={handleShowRename}>
              <EditIcon className="tw-fill-theme-900" />
              {t("Rename")}
            </ListItem>
          ) : null}
          {onRevert ? (
            <ListItem onClick={handleShowRevert}>
              <EditIcon className="tw-fill-theme-900" />
              {t("Revert")}
            </ListItem>
          ) : null}
        </List>
      </Menu>
      <RenameDialog
        open={showRename}
        onSubmit={handleRename}
        onCancel={handleCloseRename}
        name={name ?? ""}
        title={t("Rename Note Version")}
        placeholder={t("Version")}
        submitBtnText={t("Rename")}
        validationSchema={NoteVersionFormValidator}
        requiredName={false}
      />
      <ConfirmDialog
        open={showRevert}
        dialogTitle={t("Revert note version")}
        dialogContent={revertContent}
        confirmBtnText={t("Revert")}
        cancelBtnText={t("Cancel")}
        onConfirm={handleRevert}
        onCancel={handleCloseRevert}
      />
    </>
  );
};

VersionOptions.propTypes = {
  modificationId: PropTypes.number.isRequired,
  name: PropTypes.string,
  onRename: PropTypes.func,
  onRevert: PropTypes.func,
  className: PropTypes.string,
};

VersionOptions.defaultProps = {
  name: "",
};
